import { ReportFormRuleTemplate, RuleContext } from '@navstation-portal/reporting/data-model/ui';
import { findTableOfColumn, readSection } from './evaluate';
import { resolveCarryOverValue } from './resolve-carry-over-value';

/**
 * One value the template puts into one field as the form creates a report.
 *
 * The four properties after `tabId` are the `FormStoreEventPayload` of the
 * form stores, so the caller dispatches `updateFormStateValue` with the write
 * itself and adds nothing. `tabId` picks which tab's event group takes it.
 *
 * A write into a column of a section that draws one table names the cell as a
 * form control does: `fieldId` is the key of the array of rows, `index` is the
 * row, and `property` is the column. See the comment on the walk below.
 */
export interface CarryOverWrite {
  tabId: string;
  sectionId: string;
  fieldId: string;
  value: unknown;
  /** The row, for a write into a column. */
  index?: number;
  /** The column, for a write into a column. */
  property?: string;
}

/**
 * Lists every value the template carries into a new report, ready to write.
 *
 * This is the `onCreate` half of carry-over. The `onRequest` half asks for one
 * field at a time, because the user presses a button beside it, and the store
 * answers that with `resolveCarryOverValue`. This function asks for all of
 * them at once, because a new report starts once.
 *
 * **Every entry reads one context.** The caller takes the context before the
 * first write lands, and each entry resolves against that same one. So no
 * entry can read a value another entry wrote, and the order the template holds
 * its keys cannot change an answer. A template author who wants one field to
 * follow another writes the second field's value tree to read the previous
 * report as well, not the field beside it.
 *
 * **The walk drops a value that reaches nothing.** The field then keeps
 * whatever the store's init put there. This matters while the two sources overlap: the
 * init still fills most fields, and a template entry that fails to resolve
 * must not clear a value the init got right.
 *
 * The walk descends `tabs`, then `sections`, then `fields`.
 *
 * **A field can be a column.** A section that draws one table holds its
 * columns in `fields`, and the form state holds them in an array of rows, for
 * example Cargo Type in `stowageDetails.cargoes`. When the form state of the
 * section holds no field with the id, and an array holds it in its rows, the
 * walk resolves the entry once for each row that exists, with `row` in the
 * context, and writes that row. `carryOverField` then reads the same row of
 * the previous report.
 *
 * The walk does not descend `tables`. The Hoses tables start with no rows, so
 * a column entry there has no row to write into. Adding them means one more
 * loop here, with the same write shape.
 */
export function collectCarryOverWrites(
  template: ReportFormRuleTemplate | null | undefined,
  context: RuleContext
): CarryOverWrite[] {
  const writes: CarryOverWrite[] = [];
  if (!template) {
    return writes;
  }

  for (const [tabId, tab] of Object.entries(template.tabs ?? {})) {
    for (const [sectionId, section] of Object.entries(tab.sections ?? {})) {
      for (const [fieldId, field] of Object.entries(section.fields ?? {})) {
        if (field.carryOver?.onCreate !== true) {
          continue;
        }
        const table = findTableOfColumn(readSection(context.formState, tabId, sectionId), fieldId);
        if (table) {
          table.rows.forEach((_, row) => {
            const value = resolveCarryOverValue(field.carryOver?.value, { ...context, row });
            if (value !== undefined) {
              writes.push({ tabId, sectionId, fieldId: table.tableId, index: row, property: fieldId, value });
            }
          });
          continue;
        }
        const value = resolveCarryOverValue(field.carryOver.value, context);
        if (value === undefined) {
          continue;
        }
        writes.push({ tabId, sectionId, fieldId, value });
      }
    }
  }

  return writes;
}
